import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import adminService from '../../services/adminService';
import '../../styles/glass-morphism.css';

function UserManagement() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUsers();
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
      return;
    }

    try {
      await adminService.updateUser(userId, { role: newRole });
      await fetchUsers(); // Refresh the list
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This will delete all their notes and categories.`)) {
      return;
    }

    try {
      await adminService.deleteUser(userId);
      await fetchUsers(); // Refresh the list
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleResetPassword = async (userId, username) => {
    const newPassword = prompt(`Enter new password for user "${username}" (minimum 6 characters):`);

    if (!newPassword) {
      return; // User cancelled
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    const confirmPassword = prompt('Confirm the new password:');

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    try {
      await adminService.resetUserPassword(userId, newPassword);
      alert(`Password reset successfully for user "${username}"`);
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={styles.container} className="cloud-background internal-page">
      {/* Header */}
      <header style={styles.header} className="glass-container cloudnotes-app-header internal-page-header">
        <div style={styles.headerContent} className="internal-header-content">
          <h1 style={styles.logo}>User Management</h1>
          <div style={styles.headerRight} className="internal-header-actions">
            <span style={styles.username}>Welcome, {user?.username}</span>
            <button className="internal-header-action" onClick={() => navigate('/admin')} style={styles.button}>
              Dashboard
            </button>
            <button className="internal-header-action" onClick={() => navigate('/notes')} style={styles.button}>
              Notes
            </button>
            <button className="internal-header-action" onClick={handleLogout} style={styles.button}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.content}>
          {loading && <div style={styles.loadingText}>Loading...</div>}

          {error && <div style={styles.errorText}>Error: {error}</div>}

          {!loading && !error && (
            <div style={styles.tableContainer} className="glass-container">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Username</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Notes</th>
                    <th style={styles.th}>Categories</th>
                    <th style={styles.th}>Joined</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} style={styles.tr}>
                      <td style={styles.td}>{u.username}</td>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}>
                        <span style={u.role === 'admin' ? styles.roleAdmin : styles.roleUser}>
                          {u.role}
                        </span>
                      </td>
                      <td style={styles.td}>{u.note_count}</td>
                      <td style={styles.td}>{u.category_count}</td>
                      <td style={styles.td}>
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          {user.id !== u.id && (
                            <>
                              <select
                                value={u.role}
                                onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                style={styles.select}
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                              </select>
                              <button
                                onClick={() => handleResetPassword(u.id, u.username)}
                                style={styles.resetButton}
                              >
                                Reset Password
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.id, u.username)}
                                style={styles.deleteButton}
                              >
                                Delete
                              </button>
                            </>
                          )}
                          {user.id === u.id && (
                            <span style={styles.currentUserLabel}>(You)</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    boxSizing: 'border-box',
    background: '#f3f9fc',
    position: 'relative',
  },
  header: {
    flexShrink: 0,
    marginBottom: '20px',
    padding: '16px 24px',
    position: 'relative',
    zIndex: 1,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    color: 'white',
    fontSize: '1.8rem',
    fontWeight: 'bold',
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  username: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '1rem',
  },
  button: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    padding: '8px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    zIndex: 1,
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  loadingText: {
    color: '#334e68',
    fontSize: '1.5rem',
    textAlign: 'center',
    padding: '40px',
  },
  errorText: {
    color: '#b42318',
    fontSize: '1.2rem',
    textAlign: 'center',
    padding: '40px',
  },
  tableContainer: {
    padding: '24px',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    color: '#334e68',
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #b9d4e2',
    fontWeight: '600',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tr: {
    borderBottom: '1px solid #d9e9f2',
  },
  td: {
    color: '#334e68',
    padding: '16px 12px',
    fontSize: '0.95rem',
  },
  roleAdmin: {
    background: '#fef3f2',
    color: '#b42318',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  roleUser: {
    background: '#eaf8fe',
    color: '#04658f',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  select: {
    background: 'white',
    border: '1px solid #a9c7d7',
    color: '#102a43',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  resetButton: {
    background: '#0778ad',
    border: 'none',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontWeight: '500',
  },
  deleteButton: {
    background: '#b42318',
    border: 'none',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontWeight: '500',
  },
  currentUserLabel: {
    color: '#627d98',
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
};

export default UserManagement;
